import React from 'react';
import Vimeo from '@u-wave/react-vimeo';
import clsx from 'clsx';
import getYouTubeID from 'get-youtube-id';
import { useState } from 'react';
import YouTube from 'react-youtube';
import { RichText } from '../molecules/rich-text';
import { SanityLinkList } from '../molecules/sanity-link-list';
import { SanityLink } from '../../../../graphql-types';
import translate from '../../../translations';
import { Typography } from '../atoms/typography';
import { Container } from '../atoms/container';
import { Language } from '../../../interfaces';

export type Layout = 'image-left' | 'image-right' | 'image-top' | 'image-bottom';

type TitleSize = 'default' | 'large';

type VideoBlockProps = {
    titleSize?: TitleSize;
    type: 'youtube' | 'vimeo';
    title?: string;
    description?: React.ReactNode;
    videoUrl?: string;
    links?: SanityLink[];
    coverImage?: React.ReactNode;
    className?: string;
    caption?: string;
    language: Language;
};

export function VideoBlock({
    type,
    title,
    description,
    videoUrl,
    className,
    language = 'en',
    titleSize = 'default',
    coverImage,
    links,
    caption,
}: VideoBlockProps) {
    let video = null;

    const [playVideo, setPlayVideo] = useState(false);

    if (!videoUrl) {
        return null;
    }

    const isYoutubeVideo = type === 'youtube';

    if (!playVideo && !!coverImage) {
        video = (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="absolute inset-0">
                    <div className="opacity-60 h-full">{coverImage}</div>
                </div>
                <button
                    onClick={() => setPlayVideo(true)}
                    aria-label="Play video"
                    className="link-cover-all flex !h-20 !w-20 items-center justify-center rounded-full border-2 border-white lg:!h-40 lg:!w-40 z-20 relative"
                >
                    <svg
                        className="ml-2 !h-8 !w-6 lg:!h-14 lg:!w-12"
                        viewBox="0 0 62 77"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M3.74075 0.497651C2.4093 -0.349635 0.666992 0.606791 0.666992 2.18497V74.8149C0.666992 76.3931 2.40929 77.3496 3.74074 76.5023L60.8071 40.1873C62.0421 39.4014 62.0422 37.5985 60.8072 36.8126L3.74075 0.497651Z"
                            fill="white"
                        />
                    </svg>
                </button>

                {isYoutubeVideo && (
                    <div className="absolute inset-x-0 bottom-0 !h-auto w-full bg-white p-2 text-body-xs md:left-auto md:max-w-xs md:p-4 md:text-body-sm">
                        {translate(language, 'youtubePricacyPolicy')}
                        <a
                            target="_blank"
                            href="https://www.youtube.aom/t/privacy"
                            className="relative z-10 font-medium underline"
                            rel="noopener"
                        >
                            {translate(language, 'readMore')}
                        </a>
                        .
                    </div>
                )}
            </div>
        );
    } else if (isYoutubeVideo) {
        const videoId = getYouTubeID(videoUrl);

        if (videoId) {
            video = (
                <YouTube
                    videoId={videoId}
                    className="h-full w-full"
                    opts={{
                        width: '100%',
                        height: '100%',
                        playerVars: {
                            rel: 0,
                            modestbranding: 1,
                            autoplay: playVideo ? 1 : 0,
                        },
                    }}
                />
            );
        }
    } else if (type === 'vimeo') {
        video = (
            <Vimeo
                video={videoUrl}
                dnt
                autopause
                autoplay={playVideo}
                responsive
                className="[&_div]:!p-0 [&_div]:!h-full p-0 h-full"
                style={{}}
            />
        );
    }

    if (!video) {
        return null;
    }

    return (
        <Container className={className}>
            {title || description ? (
                <div
                    className={clsx('mb-6 lg:mb-8', {
                        'max-w-5xl mx-auto': isYoutubeVideo,
                        'max-w-2xl mx-auto': !isYoutubeVideo,
                    })}
                >
                    <Typography
                        variant={titleSize === 'large' ? 'hd-3xl' : 'hd-lg'}
                        desktopVariant={titleSize === 'large' ? 'hd-4xl' : 'hd-lg'}
                        as="h2"
                    >
                        {titleSize === 'large' ? title : <strong>{title}</strong>}
                    </Typography>
                    {description ? (
                        <RichText
                            value={description}
                            className={clsx({
                                'mt-3 md:mt-4 lg:mt-6': !!title,
                            })}
                        />
                    ) : null}

                    {links && links.length > 0 ? (
                        <SanityLinkList links={links} size="md" className="mt-8" />
                    ) : null}
                </div>
            ) : null}

            <div
                className={clsx({
                    'max-w-5xl mx-auto': isYoutubeVideo,
                })}
            >
                <div className="[&_video]:h-full [&_video]:w-full [&_video]:object-cover bg-black img-cover relative aspect-[16/9] overflow-hidden ">
                    {video}
                </div>

                {caption ? (
                    <Typography variant="body-sm" as={'p'} className="mt-2 text-neutral-700">
                        {caption}
                    </Typography>
                ) : null}
            </div>
        </Container>
    );
}
